'use client';

import React, { useEffect, useRef, useState } from 'react';
import '../../styles/sidenav/overlays.css';
import { usePortal } from './portal.js';

// Global class names (see overlays.css): identical in the ESM, CJS and UMD builds.
const styles = {
  popover: 'sidenav-popover',
  popoverContent: 'sidenav-popover__content',
  popoverItem: 'sidenav-popover__item',
  popoverItemActive: 'sidenav-popover__item--active',
  popoverItemIcon: 'sidenav-popover__item-icon',
  popoverItemLabel: 'sidenav-popover__item-label',
};

/**
 * Componente Popover para mostrar menús flotantes
 * Se utiliza para mostrar opciones hijas en estado collapsed
 */
const Popover = ({ 
  visible = false,
  target = null,
  items = [],
  onItemClick = null,
  onClose = null,
  placement = 'right',
  offset = 8,
  // El sidenav lo apaga: las opciones del popover son subopciones y ahí no se
  // muestran iconos. Por defecto sigue en true para no cambiar el componente
  // para quien lo use suelto.
  showIcons = true,
  className = '',
  ...props 
}) => {
  const inPortal = usePortal();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  // Calcular posición del popover
  useEffect(() => {
    if (visible && target && popoverRef.current) {
      const targetRect = target.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = targetRect.top;
      let left = targetRect.right + offset;

      // Ajustar si se sale del viewport por la derecha
      if (left + popoverRect.width > viewportWidth) {
        left = targetRect.left - popoverRect.width - offset;
      }

      // Ajustar si se sale del viewport por abajo
      if (top + popoverRect.height > viewportHeight) {
        top = viewportHeight - popoverRect.height - offset;
      }

      // Ajustar si se sale del viewport por arriba
      if (top < 0) {
        top = offset;
      }

      setPosition({ top, left });
    }
  }, [visible, target, offset]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target) && 
          target && !target.contains(event.target)) {
        onClose?.();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, target, onClose]);

  if (!visible || !target) {
    return null;
  }

  const handleItemClick = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    onItemClick?.(item, event);
    onClose?.();
  };

  return inPortal(
    <div
      ref={popoverRef}
      className={`${styles.popover} ${className}`}
      role="menu"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
      {...props}
    >
      <div className={styles.popoverContent}>
        {items.map((item, index) => (
          <button
            key={item.id || index}
            type="button"
            role="menuitem"
            aria-current={item.active ? 'page' : undefined}
            className={`${styles.popoverItem} ${item.active ? styles.popoverItemActive : ''}`}
            onClick={(e) => handleItemClick(item, e)}
            disabled={item.disabled}
          >
            {showIcons && item.icon && (
              <span className={styles.popoverItemIcon}>
                {typeof item.icon === 'string' ? (
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path d={item.icon} />
                  </svg>
                ) : (
                  item.icon
                )}
              </span>
            )}
            <span className={styles.popoverItemLabel}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Popover;
